import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { IoIosArrowUp } from 'react-icons/io';
import { IoIosArrowDown } from 'react-icons/io';
import { useTheme } from '@mui/material/styles';
import { httpsCallable } from '@firebase/functions';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import { useSelector } from 'react-redux';
import { functions } from '../../../Infrastructure/config';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers';
import { ErrorBlock, ErrorBlockSmall } from '../../CourseCreationFlow/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { calculateFirstClassDates, calculateLastClassDates } from '../../../Infrastructure/Utilities/CalculateClassDates';
import { Box, Checkbox, CircularProgress, FormControl, InputAdornment, InputLabel, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import { TimePickerDropdown } from '../TimePickerDropdown/timePickerDropdown';
import ERROR_CONSTANTS from '../../../Infrastructure/Constants/errorConstants';
import { USER_LOGS_CLIENT } from '../../../Infrastructure/Constants/logsConstant';
import useAuthentication from '../../../Infrastructure/States/onAuthStateChange';
import { convertTimestampToDay } from '../../../Infrastructure/Utilities/convertTimestampToDay';
import { updateTimesKeysWithStartTime } from '../../../Infrastructure/Utilities/classScheduleFormat';
import { generateWeekDays } from '../../../Infrastructure/Utilities/generateWeekDays';
import { generateWeekDaysFromSavedTimes } from '../../../Infrastructure/Utilities/generateWeekDaysFromSavedTimes';
import { capitalizeFirstLetter } from '../../../Infrastructure/Utilities/capitalizeFirstLetter';
import { safeClientLogEvent } from '../../../Infrastructure/Utilities/safeClientLogEvent';
import {
  StyledOutlinedTextField
} from '../../../Infrastructure/Theme/styledComponents';
import { createClassSx, mobileStickySubmitSx } from './styles';
import { Loader } from '../Loader/loader';
dayjs.extend(utc);
dayjs.extend(timezone);

export const CreateClass = ({ selectedClass, setNewClass, setShowNewCourseForm, coursesArray }) => {
    const theme = useTheme();
    const errorRef = useRef(null);
    const {userId} = useAuthentication()
    const [error, setError] = useState(null);
    const [active, setActive] = useState(false);
    const [submitCount, setSubmitCount] = useState(0);
    const ip = useSelector((state) => state.ip.address)
    const [isFormReady, setIsFormReady] = useState(false);
    const page = useSelector((state) => state.route.currentRoute)
    const [newCourseId, setNewCourseId] = useState(selectedClass?.courseId || null);
    const [isLoading, setIsLoading] = useState(false);
    const classId = selectedClass?.classId || ''
    const classSchedule = selectedClass?.course?.courseDetails?.class_schedule;
    const firstClassSchedule = classSchedule && classSchedule[classId];
    const first_class = firstClassSchedule?.first_class;
    const course_times = firstClassSchedule?.times;
    const status = firstClassSchedule?.status
    const userData = useSelector((state) => state.userData.data);
    const loading = useSelector((state) => state.userData.loading);
    const userTimezone =  userData?.account?.timezone
    const [firstClass, setFirstClass] = useState(first_class ? dayjs.tz(first_class, userTimezone) : null);
    const course_duration = firstClassSchedule?.duration;
    const [duration, setDuration] = useState(course_duration || "");
    const courseId = selectedClass?.courseId || newCourseId
    const [maxLearners, setMaxLearners] = useState(firstClassSchedule?.max_learners || '50');
    const [formData, setFormData] = useState({})
    const [hasUserEditedTimes, setHasUserEditedTimes] = useState(false);

    const handCreateClass = async () => {
        setSubmitCount((c) => c + 1);
        setError(null)
        if (courseId === null || courseId === '') {
            setError('Please Select Course');
            await safeClientLogEvent({
              ...USER_LOGS_CLIENT.CLASS_SCHEDULE_UPDATE_FAILED,
              uid: userId || null,
              source: "createClass.jsx",
              emailInfo: null,
              metadata: { reason: "Course ID is required" },
              error: "Please Select Course"
            });
            return;
        }
        if (firstClass === null || firstClass === '') {
            setError('First class date is required');
            await safeClientLogEvent({
              ...USER_LOGS_CLIENT.CLASS_SCHEDULE_UPDATE_FAILED,
              uid: userId || null,
              source: "createClass.jsx",
              emailInfo: null,
              metadata: { reason: "First class date is required" },
              error: "First class date is required"
            });
            return;
        }
        if (
            duration === null || duration === '' ||
            parseInt(duration, 10) === 0 ||
            parseInt(duration, 10) > 26
        ) {
            setError('Duration must be a number between 1 and 26 weeks.');
            await safeClientLogEvent({
              ...USER_LOGS_CLIENT.CLASS_SCHEDULE_UPDATE_FAILED,
              uid: userId || null,
              source: "createClass.jsx",
              emailInfo: null,
              metadata: { reason: "Invalid duration" },
              error: "Duration must be a number between 1 and 26 weeks."
            });
            return;
        }
        if (
            maxLearners === null || maxLearners === '' ||
            parseInt(maxLearners, 10) < 5 ||
            parseInt(maxLearners, 10) > 50
        ) {
            setError('Learners must be a number between 5 and 50.');
            await safeClientLogEvent({
              ...USER_LOGS_CLIENT.CLASS_SCHEDULE_UPDATE_FAILED,
              uid: userId || null,
              source: "createClass.jsx",
              emailInfo: null,
              metadata: { reason: "Invalid max learners" },
              error: "Learners must be a number between 5 and 50."
            });
            return;
        }
        const isAtLeastOneDaySelected = Object.values(formData).some(
            (day) => day.checked
        );
        if (!isAtLeastOneDaySelected) {
            setError('Please select at least one day.');
            await safeClientLogEvent({
              ...USER_LOGS_CLIENT.CLASS_SCHEDULE_UPDATE_FAILED,
              uid: userId || null,
              source: "createClass.jsx",
              emailInfo: null,
              metadata: { reason: "No day selected" },
              error: "Please select at least one day."
            });
            return;
        }
        const firstClassDay = firstClass.format('dddd').toLowerCase();
        const selectedDayKey = Object.keys(formData).find(
            (day) => formData[day].checked
        );
        const selectedDay = selectedDayKey ? dayjs(Number(selectedDayKey)).tz(userTimezone).format('dddd').toLowerCase() : null;
        if (selectedDay && firstClassDay !== selectedDay) {
            setError('Selected day for times must match the day of the first class.');
            await safeClientLogEvent({
              ...USER_LOGS_CLIENT.CLASS_SCHEDULE_UPDATE_FAILED,
              uid: userId || null,
              source: "createClass.jsx",
              emailInfo: null,
              metadata: { reason: "Day mismatch" },
              error: "Selected day for times must match the day of the first class."
            });
            return;
        }
        for (const day in formData) {
            const dayString = convertTimestampToDay(day, userTimezone)
            if (
                formData[day].checked &&
                (!formData[day].start || !formData[day].end)
            ) {
                setError(
                    `Fill both start and end times for ${capitalizeFirstLetter(dayString)}`
                );
                await safeClientLogEvent({
                  ...USER_LOGS_CLIENT.CLASS_SCHEDULE_UPDATE_FAILED,
                  uid: userId || null,
                  source: "createClass.jsx",
                  emailInfo: null,
                  metadata: { day, reason: "Missing times" },
                  error: `Fill both start and end times for ${capitalizeFirstLetter(dayString)}`
                });
                return;
            }
            const start = dayjs(formData[day].start);
            const end = dayjs(formData[day].end);
            if (formData[day].checked && end.isBefore(start)) {
                setError(
                    `End time must be after start time for ${capitalizeFirstLetter(dayString)}`
                );
                await safeClientLogEvent({
                  ...USER_LOGS_CLIENT.CLASS_SCHEDULE_UPDATE_FAILED,
                  uid: userId || null,
                  source: "createClass.jsx",
                  emailInfo: null,
                  metadata: { day, reason: "End time before start" },
                  error: `End time must be after start time for ${capitalizeFirstLetter(dayString)}`
                });
                return;
            }
            // Check if the gap between start and end times is greater than 30 minutes
            const timeGapHours = end.diff(start, 'hours');
            if (formData[day].checked && timeGapHours > 3) {
                setError(`Gap between start and end times for ${capitalizeFirstLetter(dayString)} should not be greater than 3 hours.`);
                await safeClientLogEvent({
                  ...USER_LOGS_CLIENT.CLASS_SCHEDULE_UPDATE_FAILED,
                  uid: userId || null,
                  source: "createClass.jsx",
                  emailInfo: null,
                  metadata: { day, reason: "Time gap too long" },
                  error: `Gap between start and end times for ${capitalizeFirstLetter(dayString)} should not be greater than 3 hours.`
                });
                return;
            }
            // Check if the gap between start and end times is greater than 30 minutes
            const timeGapMinutes = end.diff(start, 'minutes');
            if (formData[day].checked && timeGapMinutes < 30) {
                setError(`Gap between start and end times for ${capitalizeFirstLetter(dayString)} should not be less than 30 minutes.`);
                await safeClientLogEvent({
                  ...USER_LOGS_CLIENT.CLASS_SCHEDULE_UPDATE_FAILED,
                  uid: userId || null,
                  source: "createClass.jsx",
                  emailInfo: null,
                  metadata: { day, reason: "Time gap too short" },
                  error: `Gap between start and end times for ${capitalizeFirstLetter(dayString)} should not be less than 30 minutes.`
                });
                return;
            }

        }
        const times = {};
        for (const day in formData) {
            if (formData[day].checked) {
                const unixStartTimeStamp = Math.floor(new Date(formData[day]?.start).getTime());
                const unixEndTimeStamp = Math.floor(new Date(formData[day]?.end).getTime());
                times[day] = {
                    start: unixStartTimeStamp,
                    end: unixEndTimeStamp
                };
            }
        }
        const old_times = times ? times : course_times
        const sortedTimesObject =
            old_times &&
            Object.fromEntries(
                Object.entries(old_times).sort(([dayA], [dayB]) => {
                    const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                    return dayOrder.indexOf(dayA) - dayOrder.indexOf(dayB);
                })
            );
        const days = sortedTimesObject && Object.keys(sortedTimesObject);
        const propFirstClass = {
            day: days[0],
            timing: sortedTimesObject[days[0]]
        }
        const propLastClass = {
            day: days[days.length - 1],
            timing: sortedTimesObject[days[days?.length - 1]]
        }
        setError('');
        setIsLoading(true)
        try {
            const formattedTimes = updateTimesKeysWithStartTime(times, userTimezone)
            const firstClassDate = calculateFirstClassDates(propFirstClass, firstClass, userTimezone)
            const lastClassDate = calculateLastClassDates(propLastClass, firstClass, duration, userTimezone, formattedTimes)
            const updateClassScheduleStep = httpsCallable(
                functions,
                'updateclassschedulestep'
            );
            await updateClassScheduleStep({ firstClassString: firstClassDate, lastClassString: lastClassDate, duration, max_learners: maxLearners, times:formattedTimes, courseId: courseId, classId: classId, isEducatorSubmit: true, ip, page });
            setError('');
            await safeClientLogEvent({
              ...USER_LOGS_CLIENT.CLASS_SCHEDULE_UPDATE_SUCCESS,
              uid: userId || null,
              source: "createClass.jsx",
              emailInfo: null,
              metadata: { 
                courseId, 
                classId, 
                duration, 
                maxLearners,
                firstClassDate,
                lastClassDate 
              },
              error: null
            });
            setShowNewCourseForm(false)
        } catch (error) {
            setError(ERROR_CONSTANTS.GENERIC_ERROR_TEXT);
            await safeClientLogEvent({
              ...USER_LOGS_CLIENT.CLASS_SCHEDULE_UPDATE_FAILED,
              uid: userId || null,
              source: "createClass.jsx",
              emailInfo: null,
              metadata: null,
              error: error?.message || null
            });
        } finally {
            setIsLoading(false)
            setNewClass(false)
        }
    };

    const handleCheckboxChange = (dayKey) => {
        setHasUserEditedTimes(true);
        setFormData((prevFormData) => {
            const updatedDay = { ...prevFormData[dayKey] }
            const isChecked = !updatedDay.checked
            updatedDay.checked = isChecked
            return {
                ...prevFormData,
                [dayKey]: updatedDay,
            }
        })
    }
    const handleTimeChange = (dayKey, field, timeValue) => {
        if (!timeValue) return
        setHasUserEditedTimes(true);
        setFormData((prevFormData) => {
            const baseDay = dayjs(parseInt(dayKey)).tz(userTimezone);
            const updatedDateTime = baseDay
                .hour(timeValue.hour())
                .minute(timeValue.minute())
                .second(0)
                .millisecond(0)
                .valueOf();
            const updatedDay = { ...prevFormData[dayKey] };
            updatedDay[field] = updatedDateTime;
            if ((updatedDay.start || updatedDay.end) && !updatedDay.checked) {
                updatedDay.checked = true;
            }
            return {
                ...prevFormData,
                [dayKey]: updatedDay,
            }
        })
    }
    const handleFirstClassData = (date) => setFirstClass(dayjs.tz(date, userTimezone).startOf('day'))
    const handleDurationChange = (e) => {
        const newValue = e.target.value.replace(/[^0-9]/g, '');
        const newDuration = newValue ? Math.min(parseInt(newValue, 10), 26) : '';
        setDuration(newDuration);
    };
    const incrementDuration = () => {
        setDuration((prevDuration) => Math.min(prevDuration + 1, 26));
    };
    const decrementDuration = () => {
        setDuration((prevDuration) => Math.max(prevDuration - 1, 1));
    };
    const handleMaxLearnersChange = (e) => {
        const newValue = e.target.value.replace(/[^0-9]/g, '');
        const newMaxLearners = newValue ? Math.min(parseInt(newValue, 10), 50) : '';
        setMaxLearners(newMaxLearners)
    };
    const incrementMaxLearners = () => {
        setMaxLearners((prevRange) => Math.min(prevRange + 1, 50));
    };
    const decrementMaxLearners = () => {
        setMaxLearners((prevRange) => Math.max(prevRange - 1, 5));
    }
    useEffect(() => {
        if (hasUserEditedTimes) return;
        if (!course_times) {
            const updatedWeekDays = generateWeekDays(formData, userTimezone);
            setFormData(updatedWeekDays);
            setIsFormReady(true);
        } else if (firstClass && course_times) {
            const updatedWeekDays = generateWeekDaysFromSavedTimes(formData, userTimezone, course_times)
            setFormData(updatedWeekDays);
            setIsFormReady(true);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [course_times, firstClass]);
    useEffect(() => {
        if (first_class && userTimezone) {
            const userTzFirstClass = dayjs(Number(first_class)).tz(userTimezone).startOf('day');
            setFirstClass(userTzFirstClass);
        }
        // Check if course_times exists and is an object
        if (course_times && typeof course_times === 'object') {
            const updatedFormData = { ...formData };
            for (const day in formData) {
                if (course_times[day]) {
                    updatedFormData[day] = {
                        start: dayjs(course_times[day].start).format('YYYY-MM-DDTHH:mm'),
                        end: dayjs(course_times[day].end).format('YYYY-MM-DDTHH:mm'),
                        checked: true,
                    };
                }
            }
            setFormData(updatedFormData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userTimezone, first_class]);
    useEffect(() => {
        const classStatus = selectedClass?.courseClass?.status;
        if (classStatus === 'pending' || classStatus === 'active' || classStatus === 'archived' || classStatus === 'denied') {
            setActive(true);
        } else {
            setActive(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedClass?.course?.courseDetails?.status]);

    useEffect(() => {
        if (error && errorRef.current) {
            errorRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
        }
    }, [submitCount, error]);
    const handleChange = (event) => {
        setNewCourseId(event.target.value);
    };
    return (
        <Box sx={createClassSx.root}>
            <form>
                {loading || isLoading ? (
                    <Loader height='70vh'/>
                ) : (
                    <Box>
                        <Grid
                            container
                            sx={createClassSx.gridContainer}
                        >
                            <Grid lg={5} md={10} sm={12} xs={12} sx={createClassSx.leftGrid}>
                                <Box
                                    sx={createClassSx.topRow}
                                    gap={1}
                                >
                                    <Box width={'100%'}>
                                        <Typography variant='body1' color='initial' pb={2}>
                                            Select Course
                                        </Typography>
                                        <FormControl fullWidth>
                                            <InputLabel id="demo-simple-select-label">Select Course</InputLabel>
                                            <Select
                                                labelId="demo-simple-select-label"
                                                id="demo-simple-select"
                                                value={newCourseId}
                                                label="Select Course"
                                                onChange={handleChange}
                                                disabled={Boolean(selectedClass?.courseId)}
                                                MenuProps={{
                                                    disableScrollLock: true
                                                }}
                                            >
                                                 {coursesArray.filter(([_, courseDetails]) => {
                                                    if (!selectedClass?.courseId) {
                                                        return courseDetails?.status === "active"
                                                    }
                                                    return true
                                                    })
                                                    .map(([courseId, courseDetails]) => (
                                                    <MenuItem key={courseId} value={String(courseId)}
                                                        sx={createClassSx.menuItem}
                                                        >
                                                        <Tooltip
                                                            title={courseDetails?.courseDetails?.basics?.title}
                                                            placement="top"
                                                            enterTouchDelay={0}
                                                            leaveTouchDelay={3000}
                                                        >
                                                            <Typography noWrap>
                                                                {courseDetails?.courseDetails?.basics?.title}
                                                            </Typography>
                                                        </Tooltip>
                                                    </MenuItem>
                                                ))
                                                }
                                            </Select>
                                        </FormControl>
                                    </Box>
                                    <Box pt={3} width={'100%'}>
                                        <Typography variant='body1' color='initial' pb={2}>
                                            Date of first class
                                        </Typography>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                variant='outlined'
                                                label='Date of first class'
                                                value={firstClass}
                                                onChange={handleFirstClassData}
                                                disablePast
                                                fullWidth
                                                disabled={active}
                                                format="dddd, MMM D, YYYY" 
                                                sx={createClassSx.datePicker}
                                                shouldDisableDate={(date) => {
                                                    if (selectedClass && selectedClass.status !== "in_progress") {
                                                       return false
                                                    }
                                                    const today = dayjs().startOf('day');
                                                    const oneWeekFromToday = today.add(7, 'day');
                                                    return date.isBefore(oneWeekFromToday);
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </Box>
                                    <Box pt={3} width={{xs:"100%", sm:'60%'}}>
                                        <Typography variant='body1' color='initial' pb={2}>
                                            Course Duration
                                        </Typography>
                                        <StyledOutlinedTextField
                                            id='outlined-number'
                                            label='Weeks'
                                            type='number'
                                            fullWidth
                                            variant='outlined'
                                            onChange={handleDurationChange}
                                            value={duration}
                                            disabled={active}
                                            InputProps={{
                                                onKeyPress: (event) => {
                                                    if (
                                                        isNaN(event.key) ||
                                                        (event.key === '-' && event.code === 'ArrowDown')
                                                    ) {
                                                        event.preventDefault();
                                                    }
                                                },
                                                endAdornment: (
                                                    <InputAdornment position='end'>
                                                        <Box
                                                            sx={createClassSx.adornmentWrapper}
                                                        >
                                                            <IoIosArrowUp
                                                                onClick={!selectedClass?.courseId ? incrementDuration : null}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    fontSize: '20px',
                                                                    ':hover': { color: 'secondary' },
                                                                }}
                                                            />
                                                            <IoIosArrowDown
                                                                onClick={!selectedClass?.courseId ? decrementDuration : null}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    fontSize: '20px',
                                                                    hover: { color: 'primary' },
                                                                }}
                                                            />
                                                        </Box>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Box>

                                    <Box pt={3} width={{xs:"100%", sm:'60%'}}>
                                        <Typography variant='body1' color='initial' pb={2}>
                                            Max Learners
                                        </Typography>
                                        <StyledOutlinedTextField
                                            id='maxLearners'
                                            label='Max Learners'
                                            type='number'
                                            fullWidth
                                            variant='outlined'
                                            onChange={handleMaxLearnersChange}
                                            value={maxLearners}
                                            disabled={active}
                                            InputProps={{
                                                onKeyPress: (event) => {
                                                    if (
                                                        isNaN(event.key) ||
                                                        (event.key === '-' && event.code === 'ArrowDown')
                                                    ) {
                                                        event.preventDefault();
                                                    }
                                                },
                                                endAdornment: (
                                                    <InputAdornment position='end'>
                                                        <Box
                                                            sx={createClassSx.adornmentWrapper}
                                                        >
                                                            <IoIosArrowUp
                                                                onClick={!active ? incrementMaxLearners : null}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    fontSize: '20px',
                                                                    ':hover': { color: 'secondary' },
                                                                }}
                                                            />
                                                            <IoIosArrowDown
                                                                onClick={!active ? decrementMaxLearners : null}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    fontSize: '20px',
                                                                    hover: { color: 'primary' },
                                                                }}
                                                            />
                                                        </Box>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Box>
                                    <Box sx={createClassSx.submitDesktopWrapper}>
                                        {
                                            !(status === 'pending' || status === 'denied' || status === 'archived' || status === 'active') && (
                                                <Box
                                                    display={'flex'} justifyContent={'center'} alignItems={'center'} mt={3} onClick={handCreateClass}
                                                    sx={createClassSx.submitButton}
                                                >
                                                    <Typography variant="body1" color='white'>Submit</Typography>
                                                </Box>
                                            )
                                        }
                                        <ErrorBlock ref={errorRef}>{error}</ErrorBlock>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid lg={7} md={10} sm={12} xs={12}>
                                {/* add a text field of tz small black text, bolded  */}
                                <Typography variant='body1' color='initial' sx={createClassSx.timezoneText}>
                                    Times are in {dayjs().tz(userTimezone).format('z')}
                                </Typography>

                                {!isFormReady && course_times ? (
                                    <Box sx={createClassSx.loadingTimesWrapper}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <Box mt={2}>
                                        {formData && Object.keys(formData).map((day) => (
                                            <Box
                                                key={day}
                                                sx={createClassSx.dayRow}
                                            >
                                                <Checkbox
                                                    checked={formData[day]?.checked}
                                                    onChange={() => handleCheckboxChange(day)}
                                                    disabled={active}
                                                    sx={{...createClassSx.checkbox, color: theme.palette.primary.main,}}
                                                />
                                                <Box
                                                    display={'flex'}
                                                    alignItems={'center'}
                                                    justifyContent={'center'}
                                                    sx={createClassSx.dayCard}
                                                    gap={2}
                                                    ml={2}
                                                    px={1}
                                                    width={'100%'}
                                                >
                                                    <Box
                                                        sx={createClassSx.dayLabelWrapper}
                                                    >
                                                        <Typography
                                                            variant='body1'
                                                            color='initial'
                                                            textAlign={{ xs: 'center', md: 'start' }}
                                                            pl={{ md: 2 }}
                                                            textTransform={'capitalize'}
                                                            sx={createClassSx.dayLabel}
                                                        >
                                                            {convertTimestampToDay(day, userTimezone)}
                                                            <Typography
                                                                component="span"
                                                                sx={createClassSx.colon}
                                                            >
                                                                :
                                                            </Typography>
                                                        </Typography>
                                                    </Box>
                                                    <Box
                                                        display={'flex'}
                                                        justifyContent={'space-between'}
                                                        alignItems={'center'}
                                                        flexDirection={{
                                                            xs: 'column',
                                                            sm: 'row',
                                                            md: 'row',
                                                            lg: 'row',
                                                        }}
                                                        sx={createClassSx.timePickersRow}
                                                    >
                                                        {/* Start Time Dropdown */}
                                                        <TimePickerDropdown
                                                            label="Start Time"
                                                            value={formData[day]?.start ? dayjs(formData[day].start).tz(userTimezone) : null}
                                                            onChange={(value) => handleTimeChange(day, 'start', value)}
                                                            disabled={active}
                                                            width={140}
                                                        />

                                                        {/* End Time Dropdown */}
                                                        <TimePickerDropdown
                                                            label="End Time"
                                                            value={formData[day]?.end ? dayjs(formData[day].end).tz(userTimezone) : null}
                                                            onChange={(value) => handleTimeChange(day, 'end', value)}
                                                            disabled={active}
                                                            width={140}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                                <Box sx={createClassSx.mobileErrorWrapper}>
                                    <ErrorBlockSmall ref={errorRef} sx={createClassSx.mobileErrorText}>
                                        {error}
                                    </ErrorBlockSmall>
                                </Box>
                                <MobileStickySubmit
                                    onSubmit={handCreateClass}
                                    disabled={status === 'pending' || status === 'denied' || status === 'archived' || status === 'active'}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </form>
        </Box>
    );
};


export const MobileStickySubmit = ({ onSubmit, disabled }) => {
    const [footerOffset, setFooterOffset] = useState(0);
    const footerRef = useRef(null);

    useEffect(() => {
        const footerEl = document.querySelector(".site-footer");
        if (!footerEl) return;

        footerRef.current = footerEl;

        const handleScroll = () => {
            const rect = footerEl.getBoundingClientRect();
            const windowHeight = window.innerHeight;

            if (rect.top >= windowHeight) {
                setFooterOffset(0);
            }

            else if (rect.top < windowHeight && rect.top > 0) {
                setFooterOffset(windowHeight - rect.top);
            }
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);


    return (
        <Box sx={{ ...mobileStickySubmitSx.container, bottom: `${footerOffset}px` }}>
            {!disabled && (
                <Box
                    sx={{ ...mobileStickySubmitSx.submitBox }}
                    onClick={onSubmit}
                >
                    <Typography variant="body1" color="white">
                        Submit
                    </Typography>
                </Box>
            )}
        </Box>
    );
};